import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { BG_COLORS, BORDER_COLORS } from "../../../constants";
import { checkAsins } from "../../../services/apiV2";
import {
  TAsinCheckError,
  TAsinCheckItem,
} from "../../../services/apiV2.types";
import { ResultRow } from "./ResultRow";

const ASIN_PATTERN = /^[A-Z0-9]{10}$/;
/** Matches the backend pace (1.5 calls/s) for the "about N seconds" hint. */
const SECONDS_PER_ASIN = 0.7;

/** Splits pasted text on spaces, commas, semicolons and new lines; upper-cases and de-duplicates. */
export const parseAsinInput = (text: string) => {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const asin = raw.trim().toUpperCase();
    if (!asin) continue;
    const list = ASIN_PATTERN.test(asin) ? valid : invalid;
    if (!list.includes(asin)) list.push(asin);
  }
  return { valid, invalid };
};

const ERROR_TEXT: Record<TAsinCheckError, string> = {
  not_connected: "Connect your Amazon seller account to check ASINs.",
  no_valid_asins: "None of those look like ASINs (10 letters and numbers).",
  too_many_asins: "Too many ASINs for one check.",
  already_running: "A check is already running for your account. Wait for it to finish.",
  amazon_not_authorized:
    "Amazon rejected your seller account connection. Reconnect it and try again.",
};

type Props = {
  maxAsins: number;
  /** Called after a check finishes so the tabs pick up the saved results. */
  onChecked?: () => void;
};

export const AsinCheckPanel: React.FC<Props> = ({ maxAsins, onChecked }) => {
  const [text, setText] = useState("");
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState<TAsinCheckItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { valid, invalid } = useMemo(() => parseAsinInput(text), [text]);
  const overLimit = valid.length > maxAsins;

  const handleCheck = async () => {
    setChecking(true);
    setError(null);
    setResults(null);
    try {
      const { data } = await checkAsins(valid);
      if (data.error) {
        setError(
          data.error === "too_many_asins"
            ? `Up to ${data.maxAsins} ASINs per check.`
            : ERROR_TEXT[data.error],
        );
      }
      setResults(data.items);
      // A rejected connection reloads the page so it can show its reconnect screen.
      if (data.items.length > 0 || data.error === "amazon_not_authorized")
        onChecked?.();
    } catch {
      setError("The check failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Box
      data-testid="asin-check"
      border={`1px solid ${BORDER_COLORS.main}`}
      borderRadius="4px"
      padding={{ xs: "16px", sm: "20px" }}
      bgcolor={BG_COLORS.default}
      mb="16px"
    >
      <Typography fontWeight={600} fontSize="16px">
        Check your own ASINs
      </Typography>
      <Typography fontSize="13px" color="text.secondary" mb="12px">
        Paste up to {maxAsins} ASINs to see each product's brand and whether
        you can list it.
      </Typography>

      <TextField
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"B0EXAMPLE1\nB0EXAMPLE2, B0EXAMPLE3"}
        disabled={checking}
        slotProps={{ htmlInput: { "aria-label": "ASINs to check" } }}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap="8px"
        mt="8px"
      >
        <Typography
          fontSize="13px"
          color={overLimit ? "error" : "text.secondary"}
          data-testid="asin-count"
        >
          {valid.length} / {maxAsins} ASINs
          {overLimit && ` · remove ${valid.length - maxAsins} to check`}
          {invalid.length > 0 &&
            ` · skipping ${invalid.length} that ${invalid.length === 1 ? "isn't an ASIN" : "aren't ASINs"}`}
        </Typography>
        <Stack direction="row" gap="8px">
          {(text || results) && !checking && (
            <Button
              onClick={() => {
                setText("");
                setResults(null);
                setError(null);
              }}
            >
              Clear
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            disabled={checking || valid.length === 0 || overLimit}
            onClick={handleCheck}
          >
            Check {valid.length > 0 ? valid.length : ""} ASIN
            {valid.length === 1 ? "" : "s"}
          </Button>
        </Stack>
      </Stack>

      {checking && (
        <Box mt="12px" data-testid="asin-check-progress">
          <LinearProgress aria-label="Checking ASINs" />
          <Typography fontSize="12px" color="text.secondary" mt="4px">
            Checking {valid.length} ASINs with Amazon. This takes about{" "}
            {Math.max(1, Math.ceil(valid.length * SECONDS_PER_ASIN))} seconds.
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mt: "12px" }}>
          {error}
        </Alert>
      )}

      {results && results.length > 0 && (
        <Box
          mt="12px"
          border={`1px solid ${BORDER_COLORS.main}`}
          borderRadius="4px"
        >
          {results.map((item) => (
            <ResultRow
              key={item.asin}
              testId="asin-result"
              asin={item.asin}
              brand={item.brand}
              title={item.title}
              imageUrl={item.imageUrl}
              status={item.status}
              approvalLink={item.approvalLink}
              message={item.message}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AsinCheckPanel;

